import { useEffect, useState } from 'react';
import { authHeaders, mediaUrl } from 'api/metaWhatsappApi';

/**
 * A message's media as an object URL the browser can render.
 *
 * ★ WHY NOT A PLAIN `<img src>`.
 * The media endpoint is authenticated — the token travels in a header the
 * browser will not attach to an image request — and it is served through the
 * CRM, never from a Meta URL the browser could see. So the bytes are fetched
 * here and turned into a blob URL, which is revoked the moment the bubble
 * unmounts so a private photo does not outlive the screen it was shown on.
 *
 * The upstream status is kept: 410 means the asset expired before it could be
 * fetched, 413 that it was over Meta's limit, and the bubble says so instead of
 * showing a broken image.
 */
export const MEDIA_ERROR_TEXT = {
	410: 'This file expired before it could be saved.',
	413: 'This file is too large to display.',
	404: 'This file is no longer available.',
	// ★ NAMED AS A PERMISSION, because that is what it is. Downloading media is
	// a grant of its own, separate from opening the conversation, so a role can
	// be configured to read a thread and be refused every attachment in it —
	// and "you cannot open this file" sent people looking at the file.
	403: 'You do not have permission to open attachments in this conversation.',
	// The conversation has no channel id to fetch against. Previously unmapped,
	// so it read as the generic "could not be loaded".
	409: 'This conversation is not linked to a WhatsApp number yet.',
	503: 'WhatsApp needs reconnecting for this workspace. Ask an administrator.',
};

export default function useMediaObjectUrl({ conversationId, wamid, enabled = true }) {
	const [state, setState] = useState({ url: null, status: 'idle', error: null });

	useEffect(() => {
		if (!enabled || !conversationId || !wamid) return undefined;

		let objectUrl = null;
		let cancelled = false;
		const controller = new AbortController();
		setState({ url: null, status: 'loading', error: null });

		(async () => {
			try {
				const response = await fetch(mediaUrl(conversationId, wamid), {
					headers: authHeaders(),
					signal: controller.signal,
				});
				if (!response.ok) {
					if (!cancelled) setState({ url: null, status: 'error', error: response.status });
					return;
				}
				const blob = await response.blob();
				if (cancelled) return;
				objectUrl = URL.createObjectURL(blob);
				setState({ url: objectUrl, status: 'ready', error: null });
			} catch (err) {
				if (!cancelled && err?.name !== 'AbortError') {
					setState({ url: null, status: 'error', error: 503 });
				}
			}
		})();

		return () => {
			cancelled = true;
			controller.abort();
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [conversationId, wamid, enabled]);

	return state;
}
